import {
  LEVEL_CONTROL_DEFAULTS,
  getLevelIdFromValue,
} from '@/school-management/utils/levelControl';

export type LevelTestGateInput = {
  /** `progress.userLevel !== undefined` - i.e. does the student have any level at all yet. */
  hasLevel: boolean;
  /** `organizationDetails.groupSettings.placementTestRequired`, before defaulting. */
  placementTestRequired: boolean | null | undefined;
  /** `organizationDetails.groupSettings.defaultStartingLevel`, before normalizing. */
  defaultStartingLevel: string | number | null | undefined;
  /** Redux `auth.placementTestCompleted` - true only after a real Level Test completion. */
  placementTestCompleted: boolean;
  /** `progress.completedActivities > 0` - has the student actually played anything yet. */
  hasProgress: boolean;
};

export type LevelTestGateDecision =
  | { route: 'level-test' }
  | { route: 'blocked' }
  | { route: 'dashboard'; presetLevel?: number };

export const resolveLevelTestGate = ({
  hasLevel,
  placementTestRequired,
  defaultStartingLevel,
  placementTestCompleted,
  hasProgress,
}: LevelTestGateInput): LevelTestGateDecision => {
  const isPlacementTestRequired =
    placementTestRequired ?? LEVEL_CONTROL_DEFAULTS.placementTestRequired;

  if (!hasLevel) {
    if (!isPlacementTestRequired) {
      // Scenario B: the class bypasses the placement test, so the class default is the only
      // thing that can give this student a level.
      const presetLevel = getLevelIdFromValue(defaultStartingLevel);
      if (presetLevel === null) {
        // No placement-test result, no class default, no manual level - there is no level to
        // start them on. Previously this silently fell back to level 1, dropping the student
        // into content nobody assigned them; the spec requires blocking instead.
        return { route: 'blocked' };
      }
      return { route: 'dashboard', presetLevel };
    }
    // Scenario A: no level yet, but the test is required - they can go take it.
    return { route: 'level-test' };
  }

  if (isPlacementTestRequired && !placementTestCompleted && !hasProgress) {
    // Scenario C: a level was assigned (real test or class-default bypass) but the student
    // never actually played anything under it, so re-gating them is safe. Once they have real
    // progress, re-enabling the class's placement test must not send them back through it -
    // that would distort progress already earned under their current level.
    return { route: 'level-test' };
  }

  return { route: 'dashboard' };
};
